import { useState } from 'react';
import {
  Avatar, Box, Button, Card, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, alpha,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { CreateClientDialog } from '../../components/CreateClientDialog';

interface Client { id: number; email: string; full_name: string; status: string; access_level: string; created_at: string; }

const statusColor: Record<string, string> = {
  ACTIVE: '#16c784', PENDING: '#f5a623', INACTIVE: '#8b97b0', SUSPENDED: '#ea3943',
};
const accessColor: Record<string, string> = { TRADE: '#16c784', VIEW_ONLY: '#f5a623' };
const accessLabel: Record<string, string> = { TRADE: 'Trade', VIEW_ONLY: 'View Only' };

export default function ClientsPage() {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => (await api.get('/clients')).data.data.items as Client[],
  });

  const clients = data ?? [];

  return (
    <Box>
      <PageHeader
        title="Clients"
        subtitle="Manage accounts, status & exchange API keys"
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Chip
              label={`${clients.length} total`}
              sx={{
                fontWeight: 600,
                color: 'primary.light',
                backgroundColor: (t) => alpha(t.palette.primary.main, 0.12),
                border: (t) => `1px solid ${alpha(t.palette.primary.main, 0.3)}`,
              }}
            />
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
              New Client
            </Button>
          </Box>
        }
      />

      <Card>
        <TableContainer>
          <Table sx={{ minWidth: 560, '& tbody tr': { cursor: 'pointer' }, '& tbody tr:hover': { backgroundColor: 'rgba(255,255,255,0.03)' } }}>
            <TableHead>
              <TableRow>
                <TableCell>Client</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Access</TableCell>
                <TableCell align="right">Joined</TableCell>
                <TableCell padding="checkbox" />
              </TableRow>
            </TableHead>
            <TableBody>
              {clients.map((c) => {
                const color = statusColor[c.status] ?? '#8b97b0';
                return (
                  <TableRow key={c.id} hover onClick={() => navigate(`/admin/clients/${c.id}`)}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ width: 34, height: 34, fontSize: 13, fontWeight: 700, bgcolor: alpha('#4f8cff', 0.2), color: 'primary.light' }}>
                          {c.full_name?.[0]?.toUpperCase() ?? '?'}
                        </Avatar>
                        <Typography sx={{ fontWeight: 600 }}>{c.full_name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{c.email}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={c.status}
                        sx={{ color, backgroundColor: alpha(color, 0.14), border: `1px solid ${alpha(color, 0.35)}` }}
                      />
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const ac = accessColor[c.access_level] ?? '#8b97b0';
                        return (
                          <Chip
                            size="small"
                            label={accessLabel[c.access_level] ?? c.access_level ?? '—'}
                            sx={{ color: ac, backgroundColor: alpha(ac, 0.14), border: `1px solid ${alpha(ac, 0.35)}` }}
                          />
                        );
                      })()}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary' }}>
                      {c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell padding="checkbox">
                      <ChevronRightIcon sx={{ color: 'text.secondary' }} />
                    </TableCell>
                  </TableRow>
                );
              })}
              {!clients.length && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                    No clients yet — create your first one.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <CreateClientDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </Box>
  );
}
